import { useTheme } from '@emotion/react';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobArchitectureItemStatistics } from '~/shared/data-contract/job-architecture';
import { IconLevel, IconRole, PersonCircleIcon } from '~/ui/core/icons';

import { ChildCount, ChildStats, ProgressIndicator, Separator, SimpleCount } from './Status';

const calculateProgress = (count: number, total: number): number => {
    return total > 0 ? count / total : 0;
};

export type ArchitectureStatsRowProps = {
    readonly isActive?: boolean;
    readonly statistics: JobArchitectureItemStatistics;
};

export const ArchitectureStatsRow: FunctionComponent<ArchitectureStatsRowProps> = ({
    isActive = false,
    statistics,
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Removing complexity isn't worth it
}) => {
    const theme = useTheme();
    const { translate } = useContext(ApplicationContext);

    const hasHierarchyStats =
        typeof statistics.familiesCount === 'number' &&
        typeof statistics.clustersCount === 'number' &&
        typeof statistics.rolesCount === 'number' &&
        typeof statistics.levelsCount === 'number';

    if (!hasHierarchyStats) {
        return null;
    }

    const approvedRoleProgress = calculateProgress(statistics.approvedRoles ?? 0, statistics.rolesCount ?? 0);
    const publishedRoleProgress = calculateProgress(statistics.publishedRoles ?? 0, statistics.rolesCount ?? 0);
    const approvedLevelProgress = calculateProgress(statistics.approvedLevels ?? 0, statistics.levelsCount ?? 0);
    const publishedLevelProgress = calculateProgress(statistics.publishedLevels ?? 0, statistics.levelsCount ?? 0);

    return (
        <ChildStats>
            <ChildCount>
                {statistics.familiesCount ?? 0} {translate('overwrites.jobFamily.many')}
            </ChildCount>
            <ChildCount>
                {statistics.clustersCount ?? 0} {translate('overwrites.jobCluster.many')}
            </ChildCount>
            <Separator />
            <SimpleCount
                count={statistics.rolesCount ?? 0}
                data-testid="roles-count"
                tooltip={translate('pages.job-architecture-v2.statistics.role-count')}
            >
                <IconRole size={12} />
            </SimpleCount>
            <SimpleCount
                count={statistics.reviewerRoles ?? 0}
                data-testid="reviewer-roles-count"
                tooltip={translate('pages.job-architecture-v2.statistics.roles-with-reviewers')}
            >
                <PersonCircleIcon />
            </SimpleCount>
            <ProgressIndicator
                colorBase={theme.colors.info[100]}
                colorFill={theme.colors.info[500]}
                count={statistics.approvedRoles ?? 0}
                data-testid="approved-roles-count"
                icon="review"
                isActive={isActive}
                progress={approvedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.reviewed-roles')}
            />
            <ProgressIndicator
                colorBase={isActive ? theme.colors.white[200] : theme.colors.success[100]}
                colorFill={isActive ? theme.colors.white[200] : theme.colors.success[500]}
                count={statistics.publishedRoles ?? 0}
                data-testid="published-roles-count"
                icon="publish"
                isActive={isActive}
                progress={publishedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.published-roles')}
            />
            <Separator />
            <SimpleCount
                count={statistics.levelsCount ?? 0}
                data-testid="levels-count"
                tooltip={translate('pages.job-architecture-v2.statistics.level-count')}
            >
                <IconLevel size={12} />
            </SimpleCount>
            <SimpleCount
                count={statistics.reviewerLevels ?? 0}
                data-testid="reviewer-levels-count"
                tooltip={translate('pages.job-architecture-v2.statistics.levels-with-reviewers')}
            >
                <PersonCircleIcon />
            </SimpleCount>
            <ProgressIndicator
                colorBase={theme.colors.info[100]}
                colorFill={theme.colors.info[500]}
                count={statistics.approvedLevels ?? 0}
                data-testid="approved-levels-count"
                icon="review"
                isActive={isActive}
                progress={approvedLevelProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.reviewed-levels')}
            />
            <ProgressIndicator
                colorBase={isActive ? theme.colors.white[200] : theme.colors.success[100]}
                colorFill={isActive ? theme.colors.white[200] : theme.colors.success[500]}
                count={statistics.publishedLevels ?? 0}
                data-testid="published-levels-count"
                icon="publish"
                isActive={isActive}
                progress={publishedLevelProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.published-levels')}
            />
        </ChildStats>
    );
};
